import { Injectable } from '@angular/core';
import { APP_CONFIG } from '../../environment';
import { AgentMapper } from '../../providers/agent/agent.mapper';
import { IAgentSearchRequest } from '../../model/models/agent/agent-search-request.interface';
import { IAgentCityEntity } from '../../model/api/agent/agent-city.entity.interface';
import { IAgentCity } from '../../model/models/agent/agent-city.interface';
import { IAgentEntity } from '../../model/api/agent/agent.entity.interface';
import { IAgentLocationEntity } from '../../model/api/agent/agent-location.entity.interface';
import { IAgent } from '../../model/models/agent/agent.interface';
import { IAgentLocation } from '../../model/models/agent/agent-location.interface';
import { HttpProvider } from '../../providers/http/http';
import { HttpClient } from '@angular/common/http';
import { DeliveryMethodType, InstantDeliveryTypes } from '../../model/types/delivery-method-type';
import { RouteProvider } from '../../providers/route/route';
import { TranslateService } from '@ngx-translate/core';
import { LocationProvider } from '../../providers/location/location';
import { RmtPopupComponent } from '../../components/rmt-popup/rmt-popup';

@Injectable()
export class AgentProvider {

  constructor(private httpProvider: HttpProvider,
    private http: HttpClient,
    private routeProvider: RouteProvider,
    private translate: TranslateService,
    private locationProvider: LocationProvider,) {
  }

  getAgents(request: IAgentSearchRequest) : Promise<IAgent[]> {
    return this.httpProvider.get(`${APP_CONFIG.remittanceApiBaseUrl}agents`, request, (res: IAgentEntity[]) => {
      return AgentMapper.fromAgentEntities(res);
    });
  }

  getAgentCities(request: IAgentSearchRequest) : Promise<IAgentCity[]> {
    return this.httpProvider.get(`${APP_CONFIG.remittanceApiBaseUrl}agent/cities`, request, (res: IAgentCityEntity[]) => {
      return AgentMapper.fromAgentCitiesEntity(res);
    });
  }

  getAgentLocation(agentId: number, agentLocationId) : Promise<IAgentLocation[]> {
    return this.httpProvider.get(`${APP_CONFIG.remittanceApiBaseUrl}agent/location-details/${agentId}/${agentLocationId}`, (res: IAgentLocationEntity[]) => {
      return AgentMapper.fromAgentLocationEntities(res);
    });
  }

  getAgentLocations(request: IAgentSearchRequest) : Promise<IAgentLocation[]> {
    return this.httpProvider.get(`${APP_CONFIG.remittanceApiBaseUrl}agent/locations`, request, (res: IAgentLocationEntity[]) => {
      return AgentMapper.fromAgentLocationEntities(res);
    });
  }

  getAgentLogo(url: string): Promise<any> {
    return this.httpProvider.get(url);
  }

  isSameCity(cityObject, city, state) {
    if (cityObject.cityName.toLowerCase() === city.toLowerCase() &&
        (!state || cityObject.state.toLowerCase() === state.toLowerCase() || cityObject.stateAbbreviation.toLowerCase() === state.toLowerCase())) {
      return true;
    }
    return false;
  }

  findMatchingCity(cities, city, state) {
    for (let i = 0; i < cities.length; i++) {
      if (this.isSameCity(cities[i], city, state))
        return cities[i];
    }
    return null;
  }

  async checkLocationCoords(orderPromise){
    if(orderPromise.agentTo && orderPromise.agentTo.agentLocation && orderPromise.agentTo.agentLocation.latitude && orderPromise.agentTo.agentLocation.longitude)
      return true;
    else
      return false;
  }

  showSeeLocations(orderPromise) : boolean {
    return (orderPromise.deliveryMethodInternal === DeliveryMethodType.OfficePickup && orderPromise.agentTo &&
      orderPromise.agentTo.isOpenPayment && orderPromise.agentTo.locationCount === 1 &&
      (orderPromise.agentTo.payoutType === 'AnyCorrespondent' || orderPromise.agentTo.payoutType === 'NotOpen'));
  }

  showPartners(orderPromise): boolean {
    return (
      orderPromise.deliveryMethodInternal === DeliveryMethodType.OfficePickup
        && orderPromise.agentTo
        && !orderPromise.agentTo.isOpenPayment
        && orderPromise.agentTo.locationCount == 1)
  }

  isOrderDeliveryTypeHome(orderPromise): boolean {
    return orderPromise.deliveryMethodInternal === DeliveryMethodType.HomeDelivery;
  }

  isOrderDeliveryTypeMobilePayment(orderPromise): boolean {
    return orderPromise.deliveryMethodInternal === DeliveryMethodType.MobilePayment;
  }

  showSeePartners(orderPromise) : boolean {
    return (orderPromise.deliveryMethodInternal === DeliveryMethodType.OfficePickup &&
      orderPromise.agentTo &&
      orderPromise.agentTo.isOpenPayment &&
      orderPromise.agentTo.payoutType == 'OnlySelectedAgents' &&
      orderPromise.agentTo.selectedAgentToInfoList.length > 0);
  }

  seePartners(cashPickupPartnersList){
    cashPickupPartnersList.forEach(partner => {
      partner.imageUrl = this.getSelectedAgentImage(partner.selectedAgentId)

    });
    this.routeProvider.openActionSheetNew(this.routeProvider.pages.GroupPartnersListPage, {'cashPickupPartnersList': cashPickupPartnersList})
  }

  getSelectedAgentImage(selectedAgentId) {
    return `${APP_CONFIG.remittanceApiBaseUrl}agent/logo/${selectedAgentId}?useMobile=true`
  }

  async showLocation(orderPromise, mapLocation:boolean) {
    try {
      let locations = [];
      let agentLocation = await this.getAgentLocation(orderPromise.agentTo.agentLocation.agentId, orderPromise.agentTo.agentLocation.locationId);
      locations.push(agentLocation);
      let sendToLocation = this.locationProvider.convertPayNearMeLocations(locations);

      //Set the agentName to use the correct agent name in the order, instead of the agent location name returned by the endpoint.
      if (mapLocation) {
        sendToLocation[0].agentName = orderPromise.agentTo.name;
        sendToLocation[0].distance = 0;
        sendToLocation[0].canClose = false;

        if (Object.keys(sendToLocation).length) {
          let params = {
            location: sendToLocation[0],
            searchArea: false
          }
          this.routeProvider.openModal(this.routeProvider.pages.StoresPageNew, params);
        }
        else {
          RmtPopupComponent.showError(this.translate.instant('page.storeLocatorPage.findLocationsNotFound'));
        }
      }
      else {
        sendToLocation[0].agentName = orderPromise.agentTo.name;
        sendToLocation[0].canClose = true;
        sendToLocation[0].distance = 0;

        if (Object.keys(sendToLocation).length) {
          let params = { location: sendToLocation[0] }
          this.routeProvider.openActionSheet(this.routeProvider.pages.LocationDetailPage, params);
        }
        else {
          RmtPopupComponent.showError(this.translate.instant('page.storeLocatorPage.findLocationsNotFound'));
        }
      }
    } catch(err) {
      RmtPopupComponent.showError(this.translate.instant('page.storeLocatorPage.findLocationsNotFound'));
    }
  }
}



// WEBPACK FOOTER //
// ./src/providers/agent/agent.ts